export type WorkScheduleStatus =
  | 'dated'
  | 'relative'
  | 'recurring'
  | 'unclear'
  | 'not_captured';

export type WorkScheduleKind =
  | 'single'
  | 'start_only'
  | 'end_only'
  | 'range'
  | 'multiple'
  | null;

export interface WorkSchedule {
  status: WorkScheduleStatus;
  kind: WorkScheduleKind;
  dates: string[];
}

export interface JobSchedulePresentation {
  cardText: string | null;
  detailText: string;
  caution: boolean;
}

interface CalendarDate {
  iso: string;
  date: Date;
}

const NOT_CAPTURED_TEXT = 'Not captured in StageCrew—confirm before applying.';

function parseCalendarDate(value: unknown): CalendarDate | null {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (year < 1000 || monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) {
    return null;
  }

  // Construct and validate using UTC calendar fields. Date-only values must
  // never shift to the previous day for workers browsing in Pacific time.
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, monthIndex, day);
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== monthIndex
    || date.getUTCDate() !== day
  ) {
    return null;
  }

  return { iso: value, date };
}

export function formatJobScheduleDate(value: string, long = false): string | null {
  const parsed = parseCalendarDate(value);
  if (!parsed) return null;

  return parsed.date.toLocaleDateString('en-US', {
    month: long ? 'long' : 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function datedPresentation(schedule: WorkSchedule): JobSchedulePresentation | null {
  if (schedule.kind === null || !Array.isArray(schedule.dates)) return null;

  const parsedDates = schedule.dates.map(parseCalendarDate);
  if (parsedDates.some(date => date === null)) return null;

  const dates = (parsedDates as CalendarDate[])
    .filter((date, index, all) => all.findIndex(candidate => candidate.iso === date.iso) === index)
    .sort((left, right) => left.date.getTime() - right.date.getTime());
  const shortDates = dates.map(date => formatJobScheduleDate(date.iso)).filter(Boolean) as string[];
  const longDates = dates.map(date => formatJobScheduleDate(date.iso, true)).filter(Boolean) as string[];

  if (
    (schedule.kind === 'single' || schedule.kind === 'start_only' || schedule.kind === 'end_only')
    && dates.length !== 1
  ) {
    return null;
  }
  if (schedule.kind === 'range' && dates.length !== 2) return null;
  if (schedule.kind === 'multiple' && dates.length < 2) return null;

  switch (schedule.kind) {
    case 'single':
      return {
        cardText: `Gig date: ${shortDates[0]}`,
        detailText: longDates[0],
        caution: false,
      };
    case 'start_only':
      return {
        cardText: `Starts: ${shortDates[0]}`,
        detailText: `Starts ${longDates[0]}`,
        caution: false,
      };
    case 'end_only':
      return {
        cardText: `Runs through: ${shortDates[0]}`,
        detailText: `Runs through ${longDates[0]}`,
        caution: false,
      };
    case 'range':
      return {
        cardText: `Gig dates: ${shortDates[0]} – ${shortDates[1]}`,
        detailText: `${longDates[0]} – ${longDates[1]}`,
        caution: false,
      };
    case 'multiple':
      return {
        cardText: `Gig dates: ${shortDates.join('; ')}`,
        detailText: longDates.join('; '),
        caution: false,
      };
    default:
      return null;
  }
}

/**
 * Turns the server's allowlisted schedule summary into truthful public copy.
 * Malformed contract values fail closed and no listing text is accepted here.
 */
export function getJobSchedulePresentation(
  schedule: WorkSchedule | null | undefined,
): JobSchedulePresentation {
  if (!schedule || typeof schedule !== 'object') {
    return { cardText: null, detailText: NOT_CAPTURED_TEXT, caution: false };
  }

  if (schedule.status === 'dated') {
    return datedPresentation(schedule)
      || { cardText: null, detailText: NOT_CAPTURED_TEXT, caution: false };
  }

  if (schedule.kind !== null || !Array.isArray(schedule.dates) || schedule.dates.length > 0) {
    return { cardText: null, detailText: NOT_CAPTURED_TEXT, caution: false };
  }

  switch (schedule.status) {
    case 'relative':
      return {
        cardText: 'Gig date is relative—confirm before applying.',
        detailText: 'Relative date language—confirm the exact date before applying.',
        caution: true,
      };
    case 'recurring':
      return {
        cardText: 'Schedule appears recurring—confirm dates before applying.',
        detailText: 'Recurring or ongoing—confirm the current dates before applying.',
        caution: false,
      };
    case 'unclear':
      return {
        cardText: 'Gig date is unclear—confirm before applying.',
        detailText: 'Unclear—confirm the exact date before applying.',
        caution: true,
      };
    case 'not_captured':
      return { cardText: null, detailText: NOT_CAPTURED_TEXT, caution: false };
    default:
      return { cardText: null, detailText: NOT_CAPTURED_TEXT, caution: false };
  }
}
